<template>
  <div class="rovers">
    <Section medium>
      <v-col cols="12">
        <h1>What We've Built</h1>
      </v-col>

      <v-col cols="12">
        <div class="timeline">
          <div
            v-for="(item, idx) in items"
            :key="item.year"
            class="timeline-row"
            :class="{ flip: idx % 2 === 1, visible: visibleRows[idx] }"
            ref="rows"
          >
            <div class="image-col">
              <div class="image-wrap">
                <div class="image-inner">
                  <img :src="item.image" :alt="`${item.year} ${item.title}`" />
                </div>
              </div>
              <div class="underline" />
            </div>

            <div class="text-col">
              <div class="year">{{ item.year }}</div>
              <div class="title">{{ item.title }}</div>
              <div v-if="item.placement" class="placement">{{ item.placement }}</div>

              <div class="learn-more">
                <v-btn small text color="white" @click="toggle(idx)">
                  {{ expanded[idx] ? 'Hide details' : 'Learn more' }}
                </v-btn>
              </div>
              <transition name="fade">
                <p v-if="expanded[idx] && item.description" class="desc">
                  {{ item.description }}
                </p>
              </transition>
            </div>
          </div>
        </div>
      </v-col>
    </Section>
  </div>
</template>

<script>
import Section from '@/components/Section.vue';
import imgLatency from '@/assets/rovers/latency.jpg';
import imgLethargy from '@/assets/rovers/lethargy.jpg';
import imgComplacency from '@/assets/rovers/complacency.jpg';
import imgApathy from '@/assets/rovers/apathy.jpeg';
import imgProcyon from '@/assets/rovers/procyon.png';
import imgCourage from '@/assets/rovers/courage.png';
import imgContingency from '@/assets/rovers/contingency.png';

export default {
  name: 'Rovers',
  components: {
    Section,
  },
  data() {
    return {
      items: [
        {
          year: 2025,
          title: 'Contingency',
          placement: '19/114',
          image: imgContingency,
          description: 'Our most recent Mars rover, Contingency brings too much to the table. Contingency utilizes Protocase\'s sleek blue chassis. It also features a completely new and robust robotic arm that can lift 15+ lbs with complete ease alongside minimal backlash. Contingency\'s also features a more reliable and upgraded science module utilizing black acrylic.',
        },
        {
          year: 2024,
          title: 'Latency',
          placement: '6th/102',
          image: imgLatency,
          description:
            'Heavily iterated off of Lethargy, Latency features upgrades to its electrical system, an additional drill module to drill , and object detection system.',
        },
        {
          year: 2023,
          title: 'Lethargy',
          placement: '5th/104',
          image: imgLethargy,
          description:
            'Continuing the naming tradition of a negative term + y, Lethargy revamps everything. Inspired by the Mars rovers Curiosity and Perseverance, Lethargy features a robust suspension system, differential, and chassis. Lethargy continues to still use Complacency\'s robotic arm with some minor tweaks.',
        },
        {
          year: 2022,
          title: 'Complacency',
          placement: '13th/99',
          image: imgComplacency,
          description:
            'Iterating from Apathy\'s drivetrain, Complacency heavily simplifies our Mars rover. The electrical system and science module have fully been revamped and upgraded. Additionally, Complacency utilizes a brand new arm inspired off of Apathy\'s arm',
        },
        {
          year: 2019,
          title: 'Apathy',
          placement: 'COVID',
          image: imgApathy,
          description:
            'Our Mars rover from 2019 to 2021, it utilizes a more simplified chassis and suspension system. It also features a new sleek and rugged robotic arm with a heavily simplified robotic hand using 2 fingers. Unfortunately, we were not able to compete due to COVID.',
        },
        {
          year: 2018,
          title: 'Procyon',
          placement: '33rd/95',
          image: imgProcyon,
          description: 'Named after the brightest star in the Canis Minoris constellation and after our school\'s unofficial mascot (the raccoon), Procyon is a total revamp from Courage. Procyon utilizes a metal chassis and suspension system. Its robotic arm also got upgraded, made out of metal and utilizing both rotating motors and linear actuators as well as 4 fingers for its robotic hand.',
        },
        {
          year: 2017,
          title: 'Courage',
          placement: '29th/95',
          image: imgCourage,
          description:
            'Built in Atkinson Hall in 2017 is our first Mars rover where most of it was built using 3d printed parts and acrylic. Origin uses its own custom suspension system inspired by the Spirit and Opportunity alongside a robotic hand designed after the human hand. It is also equipped with a robotic arm that utilizes linear actuators.',
        },
      ],
      expanded: {},
      visibleRows: [],
    };
  },
  mounted() {
    // Reveal rows on scroll
    const { rows } = this.$refs;
    if (!rows) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const idx = Array.prototype.indexOf.call(rows, e.target);
            this.$set(this.visibleRows, idx, true);
          }
        });
      },
      {
        rootMargin: '0px 0px -10% 0px',
        threshold: 0.1,
      },
    );

    rows.forEach((el) => io.observe(el));
  },
  methods: {
    bg(src) {
      return { backgroundImage: `url(${src})` };
    },
    toggle(i) {
      this.$set(this.expanded, i, !this.expanded[i]);
    },
  },
};
</script>

<style scoped>
.timeline {
  display: grid;
  row-gap: 28px;
}

.timeline-row {
  display: grid;
  grid-template-columns: minmax(420px, 2fr) minmax(260px, 1fr);
  gap: 28px;
  align-items: center;
  position: relative;
  opacity: 0;
  transform: translateY(16px);
  transition: opacity 600ms ease, transform 600ms ease, filter 300ms ease;
}
.timeline-row.visible {
  opacity: 1;
  transform: translateY(0);
}
.timeline-row:hover {
  filter: saturate(1.05);
}
.timeline-row.flip {
  grid-template-columns: minmax(260px, 1fr) minmax(420px, 2fr);
}
.timeline-row.flip .image-col { order: 2; }
.timeline-row.flip .text-col { order: 1; }

.image-col {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.image-wrap {
  position: relative;
  overflow: visible;
  border-radius: 12px;
  background: linear-gradient(135deg, #8b7bd9, #2d2542);
  padding: 2px;
  box-shadow: 0 10px 28px rgba(0,0,0,0.35);
  width: 70%; /* shrink images ~30% */
  transition: transform 300ms ease, box-shadow 300ms ease;
}
.image-inner {
  border-radius: inherit;
  overflow: hidden;
  background: #000;
}
.image-inner img {
  width: 100%;
  height: auto;
  display: block;
  object-fit: contain;
  transform: scale(1);
  transition: transform 600ms ease;
}
.timeline-row:hover .image-wrap { transform: translateY(-4px); }
.timeline-row:hover .image-inner img { transform: scale(1.02); }
.underline {
  height: 6px;
  width: 70%;
  margin-top: 10px;
  border-radius: 4px;
  background: linear-gradient(90deg, var(--light), var(--medium));
}

.text-col {
  background: var(--medium);
  color: white;
  padding: 22px 24px;
  position: relative;
}
.text-col::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: linear-gradient(180deg, #8b7bd9, var(--light));
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}
.year {
  font-size: 44px;
  line-height: 1;
  font-weight: 900;
  letter-spacing: 1px;
}
.title {
  margin-top: 6px;
  font-size: 24px;
  font-weight: 700;
}
.placement {
  margin-top: 6px;
  font-size: 18px;
  opacity: 0.9;
  text-decoration: underline;
}
.learn-more .v-btn {
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0;
  min-width: auto;
}
.learn-more .v-btn::after {
  content: '→';
  margin-left: 6px;
  transition: transform 200ms ease;
}
.timeline-row:hover .learn-more .v-btn::after {
  transform: translateX(2px);
}

.learn-more { margin-top: 10px; }
.desc { margin-top: 8px; opacity: 0.95; }
.fade-enter-active, .fade-leave-active { transition: opacity 200ms ease; }
.fade-enter, .fade-leave-to { opacity: 0; }

@media (max-width: 960px) {
  .timeline-row,
  .timeline-row.flip {
    grid-template-columns: 1fr;
  }
  .image-wrap { width: 100%; }
  .underline { width: 100%; }
  .text-col { padding: 18px; }
  .year { font-size: 36px; }
  .title { font-size: 20px; }
}
</style>
